import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROPERTY_TYPES = ["Detached", "Semi-Detached", "Terraced", "Bungalow", "Flat", "Other"];
const BEDROOMS = ["1", "2", "3", "4", "5", "6+"];
const HEATING = ["Gas", "Oil", "LPG", "Electric", "Other"];
const STATUS = ["Homeowner", "Landlord", "Tenant", "Other"];

const initial = {
    full_name: "",
    phone: "",
    email: "",
    postcode: "",
    property_type: "",
    bedrooms: "",
    heating_system: "",
    property_status: "",
    message: "",
    consent: false,
};

const inputCls =
    "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 transition-colors duration-200 focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-600/20";

const Field = ({ label, htmlFor, required = true, children }) => (
    <div>
        <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-semibold text-slate-800">
            {label} {required && <span className="text-emerald-600">*</span>}
        </label>
        {children}
    </div>
);

export const EnquiryForm = ({ testIdPrefix = "enquiry" }) => {
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const set = (key) => (e) => setForm({ ...form, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.consent) {
            toast.error("Please confirm you are happy for us to contact you about your enquiry.");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/enquiries`, form);
            setSubmitted(true);
            toast.success("Your enquiry has been received.");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or call us on 0333 014 0227.");
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div data-testid={`${testIdPrefix}-success`} className="flex h-full flex-col items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 p-10 text-center">
                <CheckCircle2 className="h-14 w-14 text-emerald-600" />
                <p className="mt-5 font-display text-2xl font-bold text-slate-900">Thank you.</p>
                <p className="mt-3 max-w-md text-base leading-relaxed text-slate-600">
                    Your enquiry has been received and a member of the Virtex Renewables team will be in touch.
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} data-testid={`${testIdPrefix}-form`} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-900/5 md:p-8">
            <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Full Name" htmlFor={`${testIdPrefix}-name`}>
                    <input id={`${testIdPrefix}-name`} data-testid={`${testIdPrefix}-fullname-input`} required minLength={2} value={form.full_name} onChange={set("full_name")} className={inputCls} placeholder="Your full name" autoComplete="name" />
                </Field>
                <Field label="Telephone Number" htmlFor={`${testIdPrefix}-phone`}>
                    <input id={`${testIdPrefix}-phone`} data-testid={`${testIdPrefix}-phone-input`} required minLength={7} type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="e.g. 07123 456789" autoComplete="tel" />
                </Field>
                <Field label="Email Address" htmlFor={`${testIdPrefix}-email`}>
                    <input id={`${testIdPrefix}-email`} data-testid={`${testIdPrefix}-email-input`} required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.co.uk" autoComplete="email" />
                </Field>
                <Field label="Postcode" htmlFor={`${testIdPrefix}-postcode`}>
                    <input id={`${testIdPrefix}-postcode`} data-testid={`${testIdPrefix}-postcode-input`} required minLength={3} maxLength={10} value={form.postcode} onChange={set("postcode")} className={inputCls} placeholder="e.g. M20 4PS" autoComplete="postal-code" />
                </Field>
                <Field label="Property Type" htmlFor={`${testIdPrefix}-type`}>
                    <select id={`${testIdPrefix}-type`} data-testid={`${testIdPrefix}-property-type-select`} required value={form.property_type} onChange={set("property_type")} className={inputCls}>
                        <option value="" disabled>Select property type</option>
                        {PROPERTY_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                </Field>
                <Field label="Number of Bedrooms" htmlFor={`${testIdPrefix}-bedrooms`}>
                    <select id={`${testIdPrefix}-bedrooms`} data-testid={`${testIdPrefix}-bedrooms-select`} required value={form.bedrooms} onChange={set("bedrooms")} className={inputCls}>
                        <option value="" disabled>Select bedrooms</option>
                        {BEDROOMS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                </Field>
                <Field label="Current Heating System" htmlFor={`${testIdPrefix}-heating`}>
                    <select id={`${testIdPrefix}-heating`} data-testid={`${testIdPrefix}-heating-select`} required value={form.heating_system} onChange={set("heating_system")} className={inputCls}>
                        <option value="" disabled>Select heating system</option>
                        {HEATING.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                </Field>
                <Field label="Property Status" htmlFor={`${testIdPrefix}-status`}>
                    <select id={`${testIdPrefix}-status`} data-testid={`${testIdPrefix}-status-select`} required value={form.property_status} onChange={set("property_status")} className={inputCls}>
                        <option value="" disabled>Select status</option>
                        {STATUS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                </Field>
            </div>

            <div className="mt-5">
                <Field label="Message / Additional Information" htmlFor={`${testIdPrefix}-message`} required={false}>
                    <textarea id={`${testIdPrefix}-message`} data-testid={`${testIdPrefix}-message-textarea`} rows={4} value={form.message} onChange={set("message")} className={inputCls} placeholder="Tell us anything else about your property or heating requirements (optional)" />
                </Field>
            </div>

            <label className="mt-5 flex cursor-pointer items-start gap-3" htmlFor={`${testIdPrefix}-consent`}>
                <input id={`${testIdPrefix}-consent`} data-testid={`${testIdPrefix}-consent-checkbox`} type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-0.5 h-5 w-5 rounded border-slate-300 text-emerald-600 accent-emerald-600" />
                <span className="text-sm leading-relaxed text-slate-600">
                    I agree to Virtex Renewables contacting me regarding my enquiry. <span className="text-emerald-600">*</span>
                </span>
            </label>

            <button
                type="submit"
                disabled={loading}
                data-testid={`${testIdPrefix}-submit-button`}
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-emerald-600 px-7 py-4 text-sm font-bold text-white shadow-lg shadow-emerald-600/25 transition-all duration-200 hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-70"
            >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loading ? "Sending…" : "Request My Free Assessment"}
            </button>
            <p className="mt-4 text-center text-xs leading-relaxed text-slate-500">
                Your details are used only to respond to your enquiry. See our Privacy Policy for how we handle your data.
            </p>
        </form>
    );
};
